// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { setStoredToken, clearStoredToken } from '../../utils/authFetch';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [step, setStep] = useState('password'); // 'password' | '2fa' | 'forgot' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  // Read "from" redirect from URL query param (set by ProtectedRoute via window.location.replace)
  // Avoids reading from location.state which can trigger DataCloneError on Safari.
  const searchParams = new URLSearchParams(location.search);
  const fromParam = searchParams.get('from') || '/dashboard';
  const [error, setError] = useState(searchParams.get('error') || '');
  const [message, setMessage] = useState(searchParams.get('message') || '');

  const apiFetch = async (url, body) => {
    const res = await fetch(`${BACKEND}${url}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data = {};
    try {
      const text = await res.text();
      data = text ? JSON.parse(text) : {};
    } catch (_) { /* body already consumed */ }
    if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
    return data;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    setLoading(true);
    clearStoredToken(); // clear any stale token before new login
    try {
      const data = await apiFetch('/api/auth/login', { email, password });
      // Brand accounts (verified at sign-up) log in directly; admins still get 2FA.
      if (data.requires_2fa === false && data.session_token) {
        setStoredToken(data.session_token);
        if (data.user) setUser(data.user);
        window.location.replace(fromParam);
        return;
      }
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStep('2fa');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch('/api/auth/2fa/verify', { email, code: otp, purpose: '2fa' });
      if (data.session_token) setStoredToken(data.session_token);
      if (data.user) setUser(data.user);
      // Use window.location.replace — avoids DataCloneError on Safari/iOS.
      // Never use navigate() here; it calls history.pushState which can fail on Safari.
      window.location.replace(fromParam);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleResend2FA = async () => {
    setResending(true); setError(''); setMessage('');
    try {
      const data = await apiFetch('/api/auth/resend-2fa', { email });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setMessage('New code sent.');
    } catch { setMessage('New code sent.'); }
    finally { setResending(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await apiFetch('/api/auth/forgot-password', { email });
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStep('reset');
      setMessage('Reset code sent to your email.');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await apiFetch('/api/auth/reset-password', { email, code: resetCode, new_password: newPassword });
      setStep('password');
      setMessage('Password updated. Please sign in.');
      setDevOtp('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Left branding panel — narrower */}
      <div className="hidden lg:flex lg:w-2/5 bg-[#0a0a0a] flex-col justify-between p-10 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>FashionSpyder</h1>
          <p className="text-white/40 text-xs mt-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>HR INTELLIGENCE PLATFORM</p>
        </div>
        <div className="space-y-4">
          <blockquote className="text-white/75 text-base font-light leading-relaxed" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>
            "The fastest way to find verified fashion talent — before anyone else does."
          </blockquote>
          <p className="text-white/30 text-xs">— Used by hiring teams at leading fashion houses</p>
        </div>
        <p className="text-white/15 text-xs">&copy; {new Date().getFullYear()} FashionSpyder · Flence Ecosystem</p>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-3/5 flex items-center justify-center px-6 py-8 overflow-y-auto">
        <div className="w-full max-w-[360px]">

          {/* ── Password step ── */}
          {step === 'password' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground">Welcome back</h2>
                <p className="text-muted-foreground text-sm mt-0.5">Sign in to your HR account</p>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
              {message && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm">{message}</div>}

              <form onSubmit={handleLogin} className="space-y-3.5">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1">Work Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      placeholder="you@company.com" className="pl-10 rounded-none h-10" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium uppercase tracking-wide">Password</label>
                    <button type="button" onClick={() => { setStep('forgot'); setError(''); setMessage(''); }}
                      className="text-xs text-muted-foreground hover:text-foreground">Forgot?</button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                      placeholder="Your password" className="pl-10 pr-10 rounded-none h-10" />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80 h-10">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <p className="text-center text-sm text-muted-foreground mt-5">
                Not a member yet?{' '}
                <Link to="/register" className="text-foreground font-medium hover:underline">Create an account</Link>
              </p>
            </>
          )}

          {/* ── 2FA step ── */}
          {step === '2fa' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground">Check your email</h2>
                <p className="text-muted-foreground text-sm mt-0.5">We sent a 6-digit code to <strong>{email}</strong></p>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
              {message && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm">{message}</div>}
              <form onSubmit={handle2FA} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Verification Code</label>
                  <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} required
                    placeholder="000000" className="rounded-none text-center text-2xl tracking-[0.5em] font-mono" maxLength={6} />
                </div>
                <Button type="submit" disabled={loading || otp.length < 6} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80">
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
              </form>
              <div className="flex items-center justify-between mt-4">
                <button onClick={() => setStep('password')} className="text-sm text-muted-foreground hover:text-foreground">← Back</button>
                <button onClick={handleResend2FA} disabled={resending}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50">
                  <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
              </div>
            </>
          )}

          {/* ── Forgot password step ── */}
          {step === 'forgot' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground">Reset password</h2>
                <p className="text-muted-foreground text-sm mt-0.5">Enter your work email to receive a reset code</p>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Work Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                      placeholder="you@company.com" className="pl-10 rounded-none" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
              <button onClick={() => setStep('password')} className="w-full text-center text-sm text-muted-foreground mt-4 hover:text-foreground">← Back to sign in</button>
            </>
          )}

          {/* ── Reset password step ── */}
          {step === 'reset' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground">Set new password</h2>
                <p className="text-muted-foreground text-sm mt-0.5">Enter the code sent to <strong>{email}</strong></p>
              </div>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
              {message && <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm">{message}</div>}
              {/* reset code form */}
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Reset Code</label>
                  <Input value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required
                    placeholder="000000" className="rounded-none text-center text-xl tracking-[0.5em] font-mono" maxLength={6} />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                      required placeholder="Min. 8 characters" className="pl-10 pr-10 rounded-none" />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wide mb-1.5">Confirm New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                      required placeholder="Repeat new password" className="pl-10 rounded-none" />
                  </div>
                </div>
                <Button type="submit" disabled={loading || resetCode.length < 6} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80">
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
              <button onClick={() => { setStep('forgot'); setDevOtp(''); }} className="w-full text-center text-sm text-muted-foreground mt-4 hover:text-foreground">← Request new code</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
